import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import Autofill from "../../../components/autofill/AutoFill";
import Avatar from "../../../components/avatar/Avatar";
import Calendar from "../../../components/calendar/Calendar";
import IconButton from "../../../components/button/IconButton";
import Textarea from "../../../components/form-item/Textarea";
import Toggle from "../../../components/form-item/Toggle";
import TitleBlock from "../../../components/block/TitleBlock";
import SpeechBubble from "../../../components/speech-bubble/SpeechBubble";
import SvgLoadingAnimation from "../../../components/loading/index";
import StatusIndicator from "../../../components/status-indicator/StatusIndicator";
import {
  useGetCurrentUserQuery,
  useUpdateCurrentUserMutation,
} from "../../../app/accounts/authuser";
import {
  useGetScheduleQuery,
  useUpdateScheduleMutation,
} from "../../../app/accounts/meSchedule";
import {
  CAPACITY_LEVELS,
  getCapacityLabel,
  isCapacityFresh,
} from "../../../utils/user/capacity";

const SCHEDULE_RANGE_MONTHS = 6;
const DEBOUNCE_MS = 500;
const SAVED_DISPLAY_MS = 1000;
const ERROR_DISPLAY_MS = 3000;

const CapacitySliderWrapper = styled.div`
  width: 100%;
  max-width: 240px;
`;

// スライダーの見た目は soremo_style.scss のグローバル input[type="range"]
// スタイルを土台に使い、トラックへ選択量ぶんのメーター色を重ねる（MAX で
// 100%）。&& はグローバル側 (input[type="range"]::-webkit-...) より
// 指定度を上げるための二重クラス。
const CapacitySliderInput = styled.input`
  opacity: ${({ $unset }) => ($unset ? 0.4 : 1)};

  &&::-webkit-slider-runnable-track {
    background: ${({ $fillPercent }) => `linear-gradient(
      to right,
      var(--soremo-blue) 0%,
      var(--soremo-blue) ${$fillPercent}%,
      var(--soremo-border) ${$fillPercent}%,
      var(--soremo-border) 100%
    )`};
  }

  /* 設定済みのときはつまみもメーターと同色にして、レベル 1（塗り 0%）でも
     つまみ 1 個ぶんの「メーター量」として読めるようにする。 */
  &&::-webkit-slider-thumb {
    background: ${({ $unset }) =>
      $unset ? "var(--soremo-light-gray)" : "var(--soremo-blue)"};
  }
`;

/**
 * アバター + 吹き出しのプレビュー列。オファー作成（DM）のユーザープレビュー
 * （OfferWizardOverlay の UserPreviewAvatarColumn）と同じ縦並び・中央寄せで、
 * 相手に実際に見える SpeechBubble のデザインをそのまま再現する。
 */
const CapacityPreviewColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: fit-content;
`;

const CapacityLevelSlider = ({
  value,
  onChange,
  onReset,
  avatarUrl,
  avatarName,
}) => {
  // 標準 5 段階に一致するときのみスライダー位置に対応する（-1 = 未設定、
  // または任意テキスト値）。
  const serverIndex = CAPACITY_LEVELS.findIndex((l) => l.value === value);

  // ドラッグ中につまみがサーバ確定値へスナップバックしないよう、表示位置は
  // ローカル state（draft）で持ち、確定はポインタ / キーを離した時に 1 回行う。
  const [draftIndex, setDraftIndex] = useState(
    serverIndex >= 0 ? serverIndex : null,
  );
  const draftIndexRef = useRef(serverIndex >= 0 ? serverIndex : null);

  useEffect(() => {
    const next = serverIndex >= 0 ? serverIndex : null;
    draftIndexRef.current = next;
    setDraftIndex(next);
  }, [serverIndex]);

  const handleInput = (e) => {
    const idx = Number(e.target.value) - 1;
    draftIndexRef.current = idx;
    setDraftIndex(idx);
  };

  // 未設定のままつまみを動かさず離した場合はレベル 1 として確定する
  //（スライダーへのタップ＝設定の意思とみなす）。
  const commitDraft = () => {
    const level = CAPACITY_LEVELS[draftIndexRef.current ?? 0];
    if (level) onChange(level.value);
  };

  const handleKeyUp = (e) => {
    const commitKeys = [
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
      "PageUp",
      "PageDown",
    ];
    if (commitKeys.includes(e.key)) commitDraft();
  };

  // 吹き出しにはドラッグ中の値を即時反映する。任意テキスト値（標準外）は
  // スライダー位置には対応しないが、ラベルとしてはそのまま表示する。
  const draftValue =
    draftIndex !== null ? CAPACITY_LEVELS[draftIndex]?.value : null;
  const selectedLabel = getCapacityLabel(draftValue ?? value);

  // つまみ中心の位置（= トラック上の選択位置）までをメーターとして塗る。
  // レベル 5（MAX）で 100%。未設定は 0%。
  const fillPercent =
    draftIndex !== null
      ? (draftIndex / (CAPACITY_LEVELS.length - 1)) * 100
      : 0;

  return (
    <div className="u-col u-gap16 u-w100">
      <div className="u-row u-gap8 u-w100">
        <CapacitySliderWrapper>
          <CapacitySliderInput
            type="range"
            min={1}
            max={CAPACITY_LEVELS.length}
            step={1}
            value={(draftIndex ?? 0) + 1}
            aria-label="忙しさレベル"
            $unset={draftIndex === null}
            $fillPercent={fillPercent}
            onChange={handleInput}
            onPointerUp={commitDraft}
            onKeyUp={handleKeyUp}
          />
        </CapacitySliderWrapper>
        <IconButton
          icon="restart_alt"
          title="リセット"
          ariaLabel="忙しさをリセット"
          disabled={!selectedLabel}
          onClick={onReset}
        />
      </div>

      <CapacityPreviewColumn>
        <Avatar size={40} src={avatarUrl} name={avatarName} />
        {selectedLabel ? (
          <SpeechBubble direction="top" aria-label={`忙しさ: ${selectedLabel}`}>
            {selectedLabel}
          </SpeechBubble>
        ) : (
          <span className="bodytext-11 u-text-light-gray">未設定</span>
        )}
      </CapacityPreviewColumn>
    </div>
  );
};

const formatDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const parseDate = (s) => {
  const [y, m, d] = s.split("-").map((v) => parseInt(v, 10));
  return new Date(y, m - 1, d);
};

const OfferConditions = () => {
  const { data: userData, error, isLoading } = useGetCurrentUserQuery();
  const [updateCurrentUser] = useUpdateCurrentUserMutation();
  const [updateSchedule] = useUpdateScheduleMutation();

  const role = userData?.data?.role;
  const isCreator = role === "CREATOR";

  // スケジュール取得期間 (今日 〜 +6 ヶ月)。マウント時点で固定。
  const scheduleRange = useMemo(() => {
    const from = new Date();
    const to = new Date();
    to.setMonth(to.getMonth() + SCHEDULE_RANGE_MONTHS);
    return { from: formatDate(from), to: formatDate(to) };
  }, []);

  // CREATOR ロール以外は API が 403 を返すため skip
  const { data: scheduleResp, error: scheduleError } = useGetScheduleQuery(
    scheduleRange,
    { skip: !isCreator },
  );

  // ===== ローカルフォーム state =====
  const [criteriaText, setCriteriaText] = useState("");
  const [scheduleText, setScheduleText] = useState("");
  const [ngDates, setNgDates] = useState([]);
  const [updateStatus, setUpdateStatus] = useState("idle");
  const textDebounceRef = useRef(null);
  const ngDebounceRef = useRef(null);

  // ===== サーバー値を反映 =====
  useEffect(() => {
    if (userData?.data) {
      setCriteriaText(userData.data.offer_criteria || "");
      setScheduleText(userData.data.schedule_overview || "");
    }
  }, [userData]);

  useEffect(() => {
    if (scheduleResp?.data?.items) {
      const dates = scheduleResp.data.items
        .filter((i) => i.status === "off")
        .map((i) => parseDate(i.date));
      setNgDates(dates);
    }
  }, [scheduleResp]);

  // ===== 保存ステータス遷移ヘルパー =====
  const runWithStatus = useCallback(async (asyncFn, label) => {
    try {
      setUpdateStatus("saving");
      await asyncFn();
      setUpdateStatus("saved");
      setTimeout(() => setUpdateStatus("idle"), SAVED_DISPLAY_MS);
    } catch (err) {
      console.error(`Failed to update ${label}:`, err);
      setUpdateStatus("error");
      setTimeout(() => setUpdateStatus("idle"), ERROR_DISPLAY_MS);
    }
  }, []);

  // ===== /api/me PATCH =====
  const patchField = useCallback(
    (field, value) =>
      runWithStatus(
        () => updateCurrentUser({ [field]: value }).unwrap(),
        field,
      ),
    [runWithStatus, updateCurrentUser],
  );

  const debouncedPatchField = useCallback(
    (field, value) => {
      if (textDebounceRef.current) clearTimeout(textDebounceRef.current);
      textDebounceRef.current = setTimeout(() => {
        patchField(field, value);
      }, DEBOUNCE_MS);
    },
    [patchField],
  );

  // ===== /api/me/schedule PUT =====
  const patchSchedule = useCallback(
    (dates) =>
      runWithStatus(
        () =>
          updateSchedule({
            from: scheduleRange.from,
            to: scheduleRange.to,
            items: dates.map((d) => ({ date: formatDate(d), status: "off" })),
          }).unwrap(),
        "schedule",
      ),
    [runWithStatus, updateSchedule, scheduleRange],
  );

  // ===== クライテリア =====
  const updateCriteriaText = (value) => {
    setCriteriaText(value);
    debouncedPatchField("offer_criteria", value);
  };

  // ===== 今の忙しさ =====
  const capacityIndex = userData?.data?.capacity_index || null;
  const capacityUpdatedAt = userData?.data?.capacity_index_updated_at || null;
  // 3 日以上経過していたら UI 上は未選択扱い
  const capacityValue = isCapacityFresh(capacityUpdatedAt) ? capacityIndex : null;

  const handleCapacityChange = (value) => {
    if (!value || value === capacityValue) return;
    patchField("capacity_index", value);
  };

  // リセットは空文字で PATCH する。BE は capacity_index の更新として
  // trading_updated_at を打刻しつつ空値を保存し、空値は UI 上「未設定」扱い。
  const handleCapacityReset = () => {
    if (!capacityValue) return;
    patchField("capacity_index", "");
  };

  // ===== DM 公開範囲 =====
  const acceptsOpenOffers = userData?.data?.accepts_open_offers ?? false;

  const handleOpenOfferChange = (value) => {
    if (typeof value !== "boolean") return;
    patchField("accepts_open_offers", value);
  };

  // ===== スケジュールテキスト =====
  const updateScheduleText = (value) => {
    setScheduleText(value);
    debouncedPatchField("schedule_overview", value);
  };

  // ===== NG日 =====
  const handleNgDatesChange = (dates) => {
    setNgDates(dates);

    if (ngDebounceRef.current) clearTimeout(ngDebounceRef.current);
    ngDebounceRef.current = setTimeout(() => {
      patchSchedule(dates);
    }, DEBOUNCE_MS);
  };

  // ===== クリーンアップ =====
  useEffect(() => {
    return () => {
      if (textDebounceRef.current) clearTimeout(textDebounceRef.current);
      if (ngDebounceRef.current) clearTimeout(ngDebounceRef.current);
    };
  }, []);

  if (isLoading) return <SvgLoadingAnimation />;
  if (error) return <div>データの取得に失敗しました</div>;

  return (
    <>
      <div className="u-col u-gap48 u-w100">
        <div className="u-col u-gap8 u-w100">
          <TitleBlock
            title="面識のない人からのオファー"
            description="ロールで人を探している相手の候補リストに、フォロー外でも表示されます。"
          />
          <div onClick={() => handleOpenOfferChange(!acceptsOpenOffers)}>
            <Toggle value={acceptsOpenOffers}>
              {acceptsOpenOffers ? "フォロー外の人にも見つけてもらう" : "フォロワーのみ"}
            </Toggle>
          </div>
        </div>

        <div className="u-col u-gap8 u-w100">
          <TitleBlock title="今の忙しさ" description="3日でリセット。" />
          <CapacityLevelSlider
            value={capacityValue}
            onChange={handleCapacityChange}
            onReset={handleCapacityReset}
            avatarUrl={
              userData?.data?.avatar_optimized ||
              userData?.data?.avatar_original ||
              undefined
            }
            avatarName={
              userData?.data?.display_name_jp ||
              userData?.data?.display_name_en ||
              userData?.data?.stage_name
            }
          />
        </div>

        <div className="u-col u-gap8 u-w100">
          <TitleBlock
            title="クライテリア"
            description="オファーを引き受ける基準。"
          />
          <Autofill
            group="criteria"
            nowrap={true}
            onSelectContent={updateCriteriaText}
          />
          <Textarea
            value={criteriaText}
            onChange={(e) => updateCriteriaText(e.target.value)}
            rows={3}
            placeholder="プロジェクトが私にとってエキサイティングかどうかで判断します。"
            maxLength={150}
          />
        </div>

        <div className="u-col u-gap8 u-w100">
          <TitleBlock
            title="スケジュール"
            description="作業スタイルや対応時間を共有。"
          />
          <Autofill
            group="schedule"
            nowrap={true}
            onSelectContent={updateScheduleText}
          />
          <Textarea
            value={scheduleText}
            onChange={(e) => updateScheduleText(e.target.value)}
            rows={3}
            maxLength={150}
          />
          <p className="u-text-light-gray">対応できない日を共有。</p>
          {scheduleError ? (
            <div>スケジュールの取得に失敗しました</div>
          ) : (
            <div className="u-row-center u-w100">
              <Calendar
                inline={true}
                allowInput={false}
                minDate={new Date()}
                value={ngDates}
                onChange={handleNgDatesChange}
                mode="multiple"
              />
            </div>
          )}
        </div>
      </div>

      <StatusIndicator status={updateStatus} withBottomAppBar={false} />
    </>
  );
};

export default OfferConditions;
